(function (root) {
  "use strict";

  var MIN_BPM = 20;
  var MAX_BPM = 300;
  var MIN_INTERVAL_MS = 60000 / MAX_BPM;
  var MAX_INTERVAL_MS = 60000 / MIN_BPM;
  var MAX_TAP_WINDOW = 8;
  var MIN_CONSISTENCY_INTERVALS = 5;
  var CONSISTENCY_SCALE = 1.4826;

  function failure(code, message) {
    return { ok: false, error: { code: code, message: message } };
  }

  function success(value) {
    return Object.assign({ ok: true }, value);
  }

  function isFiniteNumber(value) {
    return typeof value === "number" && Number.isFinite(value);
  }

  function validateBpm(bpm) {
    if (!isFiniteNumber(bpm) || bpm <= 0) {
      return failure("invalid_bpm", "BPM must be a finite positive number.");
    }
    if (bpm < MIN_BPM || bpm > MAX_BPM) {
      return failure("bpm_out_of_range", "BPM must be between 20 and 300.");
    }
    return success({ bpm: bpm });
  }

  function median(values) {
    var sorted = values.slice().sort(function (a, b) { return a - b; });
    var middle = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0
      ? (sorted[middle - 1] + sorted[middle]) / 2
      : sorted[middle];
  }

  function roundToOneDecimal(value) {
    return Math.round((value + Number.EPSILON) * 10) / 10;
  }

  function analyzeTaps(timestamps) {
    if (!Array.isArray(timestamps)) {
      return failure("invalid_timestamps", "Timestamps must be an array of finite, increasing numbers.");
    }
    for (var i = 0; i < timestamps.length; i += 1) {
      if (!isFiniteNumber(timestamps[i]) || timestamps[i] < 0 || (i > 0 && timestamps[i] <= timestamps[i - 1])) {
        return failure("invalid_timestamps", "Timestamps must be finite, non-negative, and strictly increasing.");
      }
    }

    var intervals = [];
    var anchorTimestamp = timestamps.length ? timestamps[0] : null;
    var event = timestamps.length < 2 ? "insufficient-sample" : "accepted";
    for (var index = 1; index < timestamps.length; index += 1) {
      var interval = timestamps[index] - anchorTimestamp;
      var currentMedian = intervals.length ? median(intervals) : null;
      var pauseThreshold = currentMedian === null
        ? 3500
        : Math.max(1500, Math.min(3500, currentMedian * 2));

      if (interval > pauseThreshold) {
        intervals = [];
        anchorTimestamp = timestamps[index];
        event = "pause-reset";
        continue;
      }
      if (interval < 100) {
        event = "ignored-double-trigger";
        continue;
      }
      // Any non-double-trigger tap becomes the next timing anchor, even when
      // its interval is out of the supported BPM range.
      anchorTimestamp = timestamps[index];
      if (interval < MIN_INTERVAL_MS || interval > MAX_INTERVAL_MS) {
        event = "out-of-range";
        continue;
      }

      intervals.push(interval);
      if (intervals.length > MAX_TAP_WINDOW) intervals.shift();
      event = "accepted";
    }

    var intervalMedian = intervals.length ? median(intervals) : null;
    var rawBpm = intervalMedian === null ? null : 60000 / intervalMedian;
    var canonicalBpm = rawBpm === null ? null : roundToOneDecimal(rawBpm);
    var consistencyPercent = null;
    var consistencyLabel = "Keep tapping";

    if (intervals.length >= MIN_CONSISTENCY_INTERVALS) {
      var deviations = intervals.map(function (value) { return Math.abs(value - intervalMedian); });
      consistencyPercent = CONSISTENCY_SCALE * median(deviations) / intervalMedian * 100;
      if (consistencyPercent < 3) consistencyLabel = "Locked";
      else if (consistencyPercent < 6) consistencyLabel = "Steady";
      else if (consistencyPercent < 10) consistencyLabel = "Uneven";
      else consistencyLabel = "Inconsistent";
    }

    return success({
      event: event,
      estimateAvailable: intervals.length > 0,
      bpm: canonicalBpm,
      rawBpm: rawBpm,
      acceptedIntervals: intervals.slice(),
      acceptedIntervalCount: intervals.length,
      consistencyPercent: consistencyPercent,
      consistencyLabel: consistencyLabel
    });
  }

  function scaleTempo(bpm, factor) {
    var validation = validateBpm(bpm);
    if (!validation.ok) return validation;
    if (!isFiniteNumber(factor) || (factor !== 0.5 && factor !== 2)) {
      return failure("invalid_factor", "Tempo factor must be 0.5 or 2.");
    }
    var result = bpm * factor;
    var resultValidation = validateBpm(result);
    if (!resultValidation.ok) {
      return failure("result_out_of_range", "The scaled tempo would be outside 20–300 BPM.");
    }
    return success({ bpm: result });
  }

  function halfTime(bpm) { return scaleTempo(bpm, 0.5); }
  function doubleTime(bpm) { return scaleTempo(bpm, 2); }

  function matchSample(sampleBpm, projectBpm) {
    var sampleValidation = validateBpm(sampleBpm);
    if (!sampleValidation.ok) return failure("invalid_sample_bpm", sampleValidation.error.message);
    var projectValidation = validateBpm(projectBpm);
    if (!projectValidation.ok) return failure("invalid_project_bpm", projectValidation.error.message);
    return success({
      tempoChangePercent: (projectBpm / sampleBpm - 1) * 100,
      durationRatio: sampleBpm / projectBpm
    });
  }

  function loopDuration(bpm, numerator, denominator, bars) {
    var bpmValidation = validateBpm(bpm);
    if (!bpmValidation.ok) return bpmValidation;
    if (!Number.isInteger(numerator) || numerator < 1 || numerator > 16) {
      return failure("invalid_numerator", "Numerator must be an integer from 1 to 16.");
    }
    if ([2, 4, 8, 16].indexOf(denominator) === -1) {
      return failure("invalid_denominator", "Denominator must be 2, 4, 8, or 16.");
    }
    if (!Number.isInteger(bars) || bars < 1 || bars > 999) {
      return failure("invalid_bars", "Bar count must be an integer from 1 to 999.");
    }
    return success({
      durationSeconds: (60 / bpm) * numerator * (4 / denominator) * bars
    });
  }

  function delayNoteValues(bpm, denominator) {
    var bpmValidation = validateBpm(bpm);
    if (!bpmValidation.ok) return bpmValidation;
    if ([4, 8, 16].indexOf(denominator) === -1) {
      return failure("invalid_note_denominator", "Note denominator must be 4, 8, or 16.");
    }
    var quarterMs = 60000 / bpm;
    var straightMs = quarterMs * (4 / denominator);
    return success({
      denominator: denominator,
      straightMs: straightMs,
      dottedMs: straightMs * 1.5,
      tripletMs: straightMs * (2 / 3)
    });
  }

  var api = Object.freeze({
    analyzeTaps: analyzeTaps,
    validateBpm: validateBpm,
    halfTime: halfTime,
    doubleTime: doubleTime,
    matchSample: matchSample,
    loopDuration: loopDuration,
    delayNoteValues: delayNoteValues
  });

  root.AudiocolourTapTempoCore = api;
}(globalThis));
